/**
 * §19 ТЗ, спека 80-admin-dashboard: даты показываются в часовом поясе объекта,
 * а не браузера. Менеджер в Москве и объект в Иркутске обязаны видеть одно
 * и то же время события — то, в котором оно произошло для объекта.
 *
 * Считается через `Intl` с явной зоной: часовой пояс машины на результат
 * не влияет ни в браузере, ни в тестах.
 */

const cache = new Map<string, Intl.DateTimeFormat>();

/** Неизвестная зона не должна ронять страницу: показываем UTC и говорим об этом. */
const FALLBACK_ZONE = 'UTC';

function formatter(timezone: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${timezone}|${JSON.stringify(options)}`;
  const cached = cache.get(key);
  if (cached) return cached;

  let created: Intl.DateTimeFormat;
  try {
    created = new Intl.DateTimeFormat('ru-RU', { ...options, timeZone: timezone });
  } catch {
    created = new Intl.DateTimeFormat('ru-RU', { ...options, timeZone: FALLBACK_ZONE });
  }
  cache.set(key, created);
  return created;
}

function isValidZone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('ru-RU', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/** Пустое значение в таблице — прочерк, а не «Invalid Date». */
export const EMPTY_VALUE = '—';

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Дата и время в зоне объекта: `15.08.2026, 12:30`. */
export function formatInProjectTz(iso: string | null | undefined, timezone: string): string {
  const date = parse(iso);
  if (!date) return EMPTY_VALUE;
  const text = formatter(timezone, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  return isValidZone(timezone) ? text : `${text} (UTC)`;
}

/** Только дата: `15.08.2026`. */
export function formatDateInProjectTz(iso: string | null | undefined, timezone: string): string {
  const date = parse(iso);
  if (!date) return EMPTY_VALUE;
  return formatter(timezone, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

/**
 * Дата, время и смещение: `15.08.2026, 12:30 (GMT+03:00)`.
 * Нужно там, где значение уезжает из интерфейса — выгрузка CSV (E9) и печать.
 */
export function formatWithOffsetInProjectTz(
  iso: string | null | undefined,
  timezone: string,
): string {
  const date = parse(iso);
  if (!date) return EMPTY_VALUE;
  const parts = formatter(timezone, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'longOffset',
  }).formatToParts(date);

  // Порядок частей задаёт локаль, поэтому берём их по типу, а не по позиции
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? '';

  const zone = part('timeZoneName') || FALLBACK_ZONE;
  return `${part('day')}.${part('month')}.${part('year')}, ${part('hour')}:${part('minute')} (${zone})`;
}

/**
 * Часовые пояса России по IANA. Список статичен: подтягивать библиотеку зон
 * ради одиннадцати строк — лишняя зависимость в бандле (§23).
 *
 * Поле выбора закрытое: свободный ввод IANA-идентификатора ломал расписание
 * одной опечаткой, а проверить её было некому. Зона, уже сохранённая у объекта
 * и не попавшая в список (наследие, зарубежный объект), добавляется в перечень
 * отдельной строкой — правка объекта не должна молча менять его пояс.
 */
export const RU_TIMEZONES: ReadonlyArray<{ zone: string; title: string }> = [
  { zone: 'Europe/Kaliningrad', title: 'Калининград (MSK−1)' },
  { zone: 'Europe/Moscow', title: 'Москва (MSK)' },
  { zone: 'Europe/Samara', title: 'Самара (MSK+1)' },
  { zone: 'Asia/Yekaterinburg', title: 'Екатеринбург (MSK+2)' },
  { zone: 'Asia/Omsk', title: 'Омск (MSK+3)' },
  { zone: 'Asia/Krasnoyarsk', title: 'Красноярск (MSK+4)' },
  { zone: 'Asia/Irkutsk', title: 'Иркутск (MSK+5)' },
  { zone: 'Asia/Yakutsk', title: 'Якутск (MSK+6)' },
  { zone: 'Asia/Vladivostok', title: 'Владивосток (MSK+7)' },
  { zone: 'Asia/Magadan', title: 'Магадан (MSK+8)' },
  { zone: 'Asia/Kamchatka', title: 'Камчатка (MSK+9)' },
];

export function isKnownTimezone(timezone: string): boolean {
  return isValidZone(timezone);
}

/**
 * Название пояса для подписи: `Asia/Vladivostok` → «Владивосток (MSK+7)».
 * Незнакомый идентификатор показывается как есть — он всё равно точнее молчания.
 */
export function timezoneTitle(timezone: string): string {
  return RU_TIMEZONES.find((item) => item.zone === timezone)?.title ?? timezone;
}

/** Смещение зоны в миллисекундах в конкретный момент — с учётом перевода часов. */
function zoneOffsetMs(date: Date, timezone: string): number {
  const parts = formatter(timezone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const at = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((candidate) => candidate.type === type)?.value ?? '0');
  // Часы в ru-RU для полуночи приходят как «24»
  const hour = at('hour') % 24;
  const asUtc = Date.UTC(at('year'), at('month') - 1, at('day'), hour, at('minute'), at('second'));
  return asUtc - date.getTime();
}

/**
 * «Стенное» время объекта → момент в UTC. `2026-08-15T09:00` в Иркутске —
 * это 02:00 UTC, а не 09:00 минус пояс браузера.
 *
 * Оператор в Москве, заводя расписание иркутскому объекту, вводит время
 * объекта: иначе окно опроса открывалось бы там на пять часов раньше,
 * чем написано на экране.
 */
export function zonedInputToIso(local: string, timezone: string): string {
  if (!local) return '';
  const naive = new Date(`${local}${local.length === 16 ? ':00' : ''}Z`);
  if (Number.isNaN(naive.getTime())) return '';
  // Смещение берётся дважды: у границы перевода часов первая оценка ошибочна
  const rough = new Date(naive.getTime() - zoneOffsetMs(naive, timezone));
  return new Date(naive.getTime() - zoneOffsetMs(rough, timezone)).toISOString();
}

/** Обратное преобразование: момент → значение для `<input type="datetime-local">`. */
export function isoToZonedInput(iso: string | null | undefined, timezone: string): string {
  const date = parse(iso);
  if (!date) return '';
  const shifted = new Date(date.getTime() + zoneOffsetMs(date, timezone));
  return shifted.toISOString().slice(0, 16);
}

const MONTH_NAME = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

/**
 * Ключ периода запуска → человеческая подпись. Сервер считает ключ
 * детерминированно (`2026-08-22`, `2026-W34`, `2026-08` — см.
 * `TimeService.periodKey`), но на экране сырой ключ читается как код:
 * «2026-W34» ни о чём не говорит без календаря (QA 22.08.2026, прогон 3).
 *
 * Неделя подписывается своим понедельником — так же, как точки тренда
 * («неделя с 17.08.2026»). Незнакомый формат возвращается как есть:
 * это подпись, а не проверка.
 */
export function periodKeyLabel(key: string): string {
  const week = /^(\d{4})-W(\d{2})$/.exec(key);
  if (week) {
    const monday = isoWeekMonday(Number(week[1]), Number(week[2]));
    const dd = String(monday.getUTCDate()).padStart(2, '0');
    const mm = String(monday.getUTCMonth() + 1).padStart(2, '0');
    return `неделя с ${dd}.${mm}.${monday.getUTCFullYear()}`;
  }
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (day) return `${day[3]}.${day[2]}.${day[1]}`;
  const month = /^(\d{4})-(\d{2})$/.exec(key);
  if (month) {
    const name = MONTH_NAME[Number(month[2]) - 1];
    if (name) return `${name} ${month[1]}`;
  }
  return key;
}

/** Понедельник недели по ISO 8601: неделя 1 — та, в которую попадает 4 января. */
function isoWeekMonday(weekYear: number, week: number): Date {
  const jan4 = new Date(Date.UTC(weekYear, 0, 4));
  // getUTCDay(): вс=0; у ISO понедельник — первый день недели
  const jan4Weekday = (jan4.getUTCDay() + 6) % 7;
  const firstMonday = new Date(jan4.getTime() - jan4Weekday * 86_400_000);
  return new Date(firstMonday.getTime() + (week - 1) * 7 * 86_400_000);
}
